import type { CpiMonth } from "@/lib/cpi.functions";
import type { NfpMonth } from "@/lib/nfp.functions";
import type { RealYieldMonth } from "@/lib/real-yield";

const fp = (x: number | null, sign = false) => (x == null ? "n/a" : `${sign && x > 0 ? "+" : ""}${x.toFixed(1)}%`);
const fk = (x: number | null) => (x == null ? "n/a" : `${x > 0 ? "+" : ""}${x.toLocaleString("en-US")}K`);

export function cpiSummary(cur: CpiMonth, prev: CpiMonth | undefined) {
  const lines: { label: string; text: string }[] = [];
  const d = prev && cur.headlineYoY != null && prev.headlineYoY != null ? cur.headlineYoY - prev.headlineYoY : null;
  lines.push({
    label: "Inflation direction",
    text:
      d == null ? `Headline inflation was ${fp(cur.headlineYoY)} over the past year.`
      : Math.abs(d) < 0.05 ? `Headline inflation held steady at ${fp(cur.headlineYoY)} year over year.`
      : d > 0 ? `Headline inflation rose to ${fp(cur.headlineYoY)} from ${fp(prev?.headlineYoY ?? null)}. Prices are accelerating.`
      : `Headline inflation eased to ${fp(cur.headlineYoY)} from ${fp(prev?.headlineYoY ?? null)}. Price pressure is cooling.`,
  });
  if (cur.coreYoY != null) {
    const cd = prev?.coreYoY != null ? cur.coreYoY - prev.coreYoY : 0;
    lines.push({
      label: "Underlying trend (Core)",
      text: `Core CPI, excluding food and energy, is ${fp(cur.coreYoY)}${Math.abs(cd) >= 0.05 ? (cd > 0 ? ", higher than last month" : ", lower than last month") : ", unchanged from last month"}. This is the measure the Federal Reserve watches most closely.`,
    });
  }
  lines.push({
    label: "What it usually means for gold",
    text:
      d != null && d > 0.05
        ? "Accelerating inflation can keep rate expectations elevated and support the dollar around the release, a short term headwind for gold. Over longer horizons, persistent inflation can support demand for gold."
        : d != null && d < -0.05
          ? "Cooling inflation can reduce rate expectations and soften the dollar, a condition that has often supported gold around the release."
          : "A stable reading usually carries limited directional information. The surprise versus forecast matters more than the level itself.",
  });
  if (cur.goldReleaseMonthPct != null)
    lines.push({ label: "How gold actually moved", text: `Gold closed the release month ${cur.goldReleaseMonthPct >= 0 ? "up" : "down"} ${Math.abs(cur.goldReleaseMonthPct).toFixed(1)}%. Many other drivers act within a month, so this is context, not cause.` });
  return lines;
}

export function nfpSummary(cur: NfpMonth, prev: NfpMonth | undefined, avg3: number | null) {
  const lines: { label: string; text: string }[] = [];
  const k = cur.payrollsK ?? 0;
  lines.push({
    label: "Hiring",
    text:
      k < 0 ? `The US economy lost ${Math.abs(k).toLocaleString("en-US")}K jobs. Hiring contracted.`
      : k < 75 ? `Employers added only ${fk(k)} jobs. Hiring is weak.`
      : k < 200 ? `Employers added ${fk(k)} jobs. Hiring is moderate.`
      : `Employers added ${fk(k)} jobs. Hiring is strong.`,
  });
  if (avg3 != null) lines.push({ label: "3 month trend", text: `The 3 month average is ${fk(avg3)} per month. One print can mislead, the trend matters more.` });
  if (cur.unemployment != null) {
    const d = prev?.unemployment != null ? r1(cur.unemployment - prev.unemployment) : 0;
    lines.push({
      label: "Unemployment",
      text: `The unemployment rate is ${cur.unemployment.toFixed(1)}%${d > 0 ? `, up ${d.toFixed(1)} point. The labour market is loosening.` : d < 0 ? `, down ${Math.abs(d).toFixed(1)} point. The labour market is tightening.` : ", unchanged from last month."}`,
    });
  }
  if (cur.wagesYoY != null) lines.push({ label: "Wages", text: `Average hourly earnings grew ${fp(cur.wagesYoY)} over the year (${fp(cur.wagesMoM, true)} on the month). Firm wage growth feeds inflation concerns.` });
  const strong = k >= 200 && (cur.unemployment ?? 0) <= (prev?.unemployment ?? 99);
  const weak = k < 75 || (prev?.unemployment != null && cur.unemployment != null && cur.unemployment > prev.unemployment);
  lines.push({
    label: "What it usually means for gold",
    text: strong
      ? "A strong labour market can keep rate expectations elevated and support the dollar, a short term headwind for gold."
      : weak
        ? "A softening labour market can lower rate expectations and weaken the dollar, a condition that has often supported gold."
        : "A mixed report usually carries limited directional information. The surprise versus forecast matters more than the level.",
  });
  if (cur.goldReleaseMonthPct != null)
    lines.push({ label: "How gold actually moved", text: `Gold closed the release month ${cur.goldReleaseMonthPct >= 0 ? "up" : "down"} ${Math.abs(cur.goldReleaseMonthPct).toFixed(1)}%. Many drivers act within a month, so this is context, not cause.` });
  return lines;
}
const r1 = (n: number) => Math.round(n * 10) / 10;

export function realYieldSummary(cur: RealYieldMonth, prev: RealYieldMonth | undefined) {
  const lines: { label: string; text: string }[] = [];
  const lvl = cur.realAvg;
  lines.push({
    label: "Real yield level",
    text:
      lvl == null
        ? "The monthly average real yield is unavailable."
        : `The 10 year TIPS yield averaged ${fp(lvl)} this month. This is the inflation adjusted return on holding US government bonds.`,
  });
  const d = cur.realChange ?? (prev?.realAvg != null && lvl != null ? Math.round((lvl - prev.realAvg) * 100) / 100 : null);
  if (d != null) {
    lines.push({
      label: "Monthly direction",
      text:
        Math.abs(d) < 0.05
          ? "Real yields were broadly unchanged versus the prior month."
          : d > 0
            ? `Real yields rose ${d.toFixed(2)} points versus the prior month. The opportunity cost of holding gold is increasing.`
            : `Real yields fell ${Math.abs(d).toFixed(2)} points versus the prior month. The opportunity cost of holding gold is decreasing.`,
    });
  }
  lines.push({
    label: "What it usually means for gold",
    text:
      lvl != null && lvl >= 2
        ? "Real yields at or above 2% are historically a headwind for gold, since bonds offer a meaningful inflation adjusted return."
        : lvl != null && lvl < 1
          ? "Low real yields reduce the opportunity cost of holding gold, a condition that has often supported the metal."
          : "Real yields in the 1% to 2% band are neutral territory. The direction of travel matters more than the level.",
  });
  if (cur.goldPct != null)
    lines.push({ label: "How gold actually moved", text: `Gold closed the month ${cur.goldPct >= 0 ? "up" : "down"} ${Math.abs(cur.goldPct).toFixed(1)}%. Many drivers act within a month, so this is context, not cause.` });
  return lines;
}

